import PropTypes from "prop-types";
import PopupComponent, {PopupActionDataObject, PopupTabDataObject} from "Core/components/PopupComponent";
import {connect} from "react-redux";
import * as pageConfig from "../../config";
import {getPageActions} from "Core/helpers/redux";
import * as pageActions from "../../actions";
import * as actions from "./actions";
import {cloneDeep, orderBy, get, isEqual} from "lodash";
import {
	icon_font_close_symbol, 
	icon_font_create_symbol, 
	icon_font_save_symbol, 
	icon_font_delete_symbol
} from "Config/app";
import {BUTTON_STYLE} from "Core/components/display/Button";
import {getArray, getBool, getString, isset} from 'Core/helpers/data';
import Acl from 'Acl/index';
import {selectors} from 'Core/store/reducers';
import {reducerStoreKey as pageReducerStoreKey} from "../../reducer";
import {reducerStoreKey} from "./reducer";
import ProjectsACL from 'Acl/projects';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in a component.
 */
const mapStateToProps = state => ({
	localeCode: selectors.i18n.getLocaleCode(state),
	customerGroupsItemId: getString(selectors[pageReducerStoreKey].getCustomerGroupsItem(state), 'id'),
	customerGroupsItemName: getString(selectors[pageReducerStoreKey].getCustomerGroupsItem(state), 'name'),
	changeInProgress: selectors[reducerStoreKey].getInProgress(state),
});

class ItemPopup extends PopupComponent {
	/**
	 * IMPORTANT! Must be defined in components that extend this abstract component like this:
	 * dirname = __dirname;
	 *
	 * @note This is done in order for automatic tab component loading to work properly.
	 */
	dirname = __dirname;

	constructor(props) {
		super(props, {
			translationPath: `${pageConfig.translationPath}.ItemPopup`,
			domPrefix: 'item-popup',
			hideSingleTab: true,

			// Custom component options
			/**
			 * Current interval to check if changing customer group is in progress
			 * @note The value entered here is an initial value that will be used when component mounts, but it will be
			 * changed based on the state of the 'changeInProgress' flag when 'checkChangeIntervalSlow' or 
			 * 'checkChangeIntervalFast' will be used. This should probably be the same value as the 
			 * 'checkChangeIntervalSlow' option.
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkChangeInterval: 60000,
			/**
			 * Interval to check if changing customer group is in progress if it is not in progress
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkChangeIntervalSlow: 60000,
			/**
			 * Interval to check if changing customer group is in progress while it is in progress
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkChangeIntervalFast: 1000,
			/**
			 * Interval identifier for an interval for checking if changing the customer group is in progress
			 */
			changeIntervalId: undefined,
		});

		this.initialState = {
			/**
			 * List of all popup tabs
			 * @type {PopupTabDataObject[]}
			 */
			tabs: [],

			/**
			 * List of all popup actions
			 * @type {PopupActionDataObject[]}
			 */
			actions: [],

			/**
			 * ID of the currently opened tab
			 * @type {string}
			 */
			currentTabId: '',
		};

		this.state = cloneDeep(this.initialState);

		// Action methods
		this.save = this.save.bind(this);
		this.saveAndClose = this.saveAndClose.bind(this);
		this.delete = this.delete.bind(this);
		
		// Change customer group methods
		this.startChangeInterval = this.startChangeInterval.bind(this);
		this.stopChangeInterval = this.stopChangeInterval.bind(this);
		this.updateChangeProgress = this.updateChangeProgress.bind(this);
	}

	componentDidMount() {
		super.componentDidMount();

		// Set the interval to periodically checking and updating change status
		this.startChangeInterval();
	}

	async componentDidUpdate(prevProps, prevState, snapshot) {
		const {localeCode, isNew, customerGroupsItemId} = this.props;

		// Update dynamic action buttons when locale code changes to update action button labels
		if (localeCode !== prevProps.localeCode) {
			setTimeout(() => this.dynamicActionButtons({isNew}));
		}

		if (customerGroupsItemId !== prevProps.customerGroupsItemId) {
			// Restart the interval to periodically checking and updating change status
			this.startChangeInterval();
			// Update change progress
			this.updateChangeProgress().then();
		}
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		this.stopChangeInterval();
	}
	

	// Component property methods ---------------------------------------------------------------------------------------
	/**
	 * Get component's ID that can be used as a DOM element id attribute value
	 * @return {string}
	 */
	getDomId() { return this.getOption('domPrefix'); }


	// Tab methods ------------------------------------------------------------------------------------------------------
	/**
	 * Update dynamic action buttons that depend on current state and props
	 * @param {boolean} [isNew] - Flag that specifies if this popup is for a new item.
	 * @return {Promise<void>}
	 */
	async dynamicActionButtons({isNew}) {
		const {projectId} = this.props;
		const canEdit = (
			!!projectId ? 
				ProjectsACL.checkPermission(ProjectsACL, projectId, ['CUSTOMER_WRITE']) : 
				Acl.checkPermission(Acl, ['CUSTOMER_WRITE'])
		);
		
		// Define all actions
		const actionDefinitions = {
			'create': new PopupActionDataObject({
				id: 'create',
				action: this.save,
				buttonProps: {
					label: this.getTranslationPath('create_action'),
					icon: icon_font_create_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 3
			}),
			'update': new PopupActionDataObject({
				id: 'update',
				action: this.save,
				buttonProps: {
					label: this.getTranslationPath('update_action'),
					icon: icon_font_save_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 3
			}),
			'update_and_close': new PopupActionDataObject({
				id: 'update_and_close',
				action: this.saveAndClose,
				buttonProps: {
					label: this.getTranslationPath('update_and_close_action'),
					icon: icon_font_save_symbol,
					displayStyle: BUTTON_STYLE.ACTION
				},
				ordinal: 2,
			}),
			'delete': new PopupActionDataObject({
				id: 'delete',
				action: this.delete,
				buttonProps: {
					label: 'general.Delete',
					icon: icon_font_delete_symbol,
					displayStyle: BUTTON_STYLE.DEFAULT
				},
				ordinal: 1
			}),
		};

		// Define actions to remove and updated based on dynamic values
		let actionsToRemove;
		let actionsToUpdate;
		if (isNew) {
			actionsToRemove = (
				canEdit ? 
					['delete', 'update', 'update_and_close'] :
					['create', 'delete', 'update', 'update_and_close']
			);
			actionsToUpdate = (
				canEdit ? 
					['create'] :
					[]
			);
		} else {
			actionsToRemove = (
				canEdit ? 
					['create'] :
					['create', 'delete', 'update', 'update_and_close']
			);
			actionsToUpdate = (
				canEdit ? 
					['delete', 'update', 'update_and_close'] :
					[]
			);
		}
		
		// Remove and update actions
		// @note You usually do not have to change anything here.
		await Promise.allSettled([
			this.removeActions(actionsToRemove),
			...actionsToUpdate.map(id => {
				const prevAction = this.getAction(id);
				const action = actionDefinitions[id];
				if (!isEqual(prevAction, action)) {
					if (this.actionExists(id)) return this.updateAction(id, action);
					else return this.addAction(action);
				}
				return Promise.resolve();
			})
		]);
	}

	/**
	 * Update dynamic tabs that depend on the current state and props
	 *
	 * @param {boolean} [isNew] - Flag that specifies if this popup is for a new item.
	 * @return {Promise<unknown>}
	 */
	async dynamicTabs({isNew}) {
		const {projectId, changeInProgress} = this.props;
		
		// Add customers tab on update dialog
		if (!isNew) {
			if (!this.tabExists('CustomersTab')) {
				await this.addTab(new PopupTabDataObject({
					id: 'CustomersTab',
					icon: 'users',
					label: this.getTranslationPath('CustomersTab.tab_name'),
					ordinal: 2
				}));
				await this.importTabComponents();
			}
		}
		// Remove customers' tab on create dialog
		else {
			if (this.tabExists('CustomersTab')) await this.removeTab('CustomersTab');
		}

		return Promise.allSettled(
			this.getTabs().map(tab => this.updateTab({
				...tab,
				componentProps: {...tab.componentProps, isNew, projectId},
				loading: (tab.id === 'CustomersTab' ? changeInProgress : false)
			}))
		);
	}

	/**
	 * Initialize popup by specifying initial tabs, actions and current tab
	 * @note If current tab is not set it will default to the first visible and valid tab. Valid tab is a tab that has a
	 * 'component' property specified (manually or automatically loaded).
	 * @return {Promise<any>} Promise that resolves to entire component local state after state is updated.
	 */
	async init() {
		const loading = this.showLoading();
		
		const isNew = getBool(this.props, 'isNew');

		// Add static actions that don't depend on the current state or props
		let actions = [
			new PopupActionDataObject({
				id: 'close',
				action: this.close,
				buttonProps: {
					label: 'general.Close',
					icon: icon_font_close_symbol
				},
				ordinal: 0
			}),
		];
		await this.setActions(actions);
		// Add dynamic actions that depend on the current state or props
		await this.dynamicActionButtons({isNew});

		// Add tabs
		await this.setTabs([
			new PopupTabDataObject({
				id: 'MainTab',
				icon: 'cog',
				label: this.getTranslationPath('MainTab.tab_name'), 
			}),
		]).then(this.importTabComponents);
		// Update dynamic tabs that depend on the current state or props
		await this.dynamicTabs({isNew});

		this.hideLoading(loading);
		
		return Promise.resolve(this.state);
	}

	/**
	 * Try to automatically load tab components from standard location for tabs that don't have components defined
	 * @note To automatically load tab components they need to be located in a 'tabs' subdirectory either as a component
	 * file (like ./tabs/InfoTab.js) or subdirectory with an index file (./tabs/InfoTab/index.js) where directory name or
	 * filename must be the tab ID.
	 *
	 * @return {Promise<any>} Promise that resolves to entire component local state after state is updated.
	 */
	importTabComponents() {
		const tabs = orderBy(this.getSortedTabs(), ['preloadPriority'], ['desc']);
		return Promise.all(tabs.map(tab => {
			if (!isset(tab.component)) return this.handleTabComponentImport(tab, import(`./tabs/${tab.id}`));
			else return Promise.resolve(this.state);
		}));
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Save item
	 * @note This method will handle both updating and creating a new item.
	 *
	 * @param {Object} allTabsData - Internal tab data object where keys are tab IDs and values are internal tabs data.
	 * @param {MouseEvent} event - Mouse click event for clicked action button DOM element.
	 * @param {'create'|'update'} actionId - ID of the clicked action.
	 * @return {Promise<*>}
	 */
	save(allTabsData, event, actionId) {
		const {
			projectId, redirectToItem, createCustomerGroupsItemAction, updateCustomerGroupsItemAction, 
			loadCustomerGroupsListAction, addSuccessMessageAction
		} = this.props;

		return this.getTabRef('MainTab').validateTab()
			.then(valid => {
				if (valid) {
					/** @type {CustomerGroupsItemDataObject} */
					let item = get(allTabsData, 'MainTab');
					
					// Set project ID if it was specified as a prop
					// @description If project ID was specified as a prop, this popup is used in the project-related page.
					if (!!projectId) item = {...item, projectId};

					// Create item
					if (actionId === 'create') {
						return this.executeAbortableAction(createCustomerGroupsItemAction, item)
							.then(createdItem => {
								if (createdItem?.id) {
									// Redirect to item URL if the item was created successfully and show a success message
									redirectToItem(createdItem.id);
                        	addSuccessMessageAction(this.t('create_success_msg'));
								
									// Reload an item list (data table) without rendering the loading overlay
									// @note This is done asynchronously on purpose because there is no reason to wait for this 
									// to finish before continuing.
									this.executeAbortableAction(loadCustomerGroupsListAction,
										projectId, undefined, undefined, undefined, undefined, undefined, false
									).then();
								}
								return createdItem;
							});
					}
					// Update item
					else if (actionId === 'update') {
						return this.executeAbortableAction(
							updateCustomerGroupsItemAction,
							getString(item, 'id', '', true),
							item
						)
							.then(response => {
								if (response) addSuccessMessageAction(this.t('update_success_msg'));
								return response;
							});
					} else {
						console.error(`Invalid customer groups item popup save method: ${actionId}`);
					}
				}
				return Promise.resolve();
			});
	}

	/**
	 * Save item and close the popup if successful
	 *
	 * @param {Object} allTabsData - Internal tab data object where keys are tab IDs and values are internal tabs data.
	 * @param {MouseEvent} event - Mouse click event for a clicked action button DOM element.
	 * @return {Promise<*>}
	 */
	saveAndClose(allTabsData, event) {
		return this.save(allTabsData, event, 'update')
			.then(response => {
				if (isset(response)) this.close();
				return response;
			});
	}

	/**
	 * Delete item
	 *
	 * @param {Object} allTabsData - Internal tab data object where keys are tab IDs and values are internal tabs data.
	 * @param {MouseEvent} event - Mouse click event for clicked action button DOM element.
	 * @param {string} actionId - ID of the clicked action.
	 * @return {Promise<*|void>}
	 */
	delete(allTabsData, event, actionId) {
		const {deleteItemAction, redirectToBase} = this.props;
		const item = get(allTabsData, 'MainTab');
		if (item && item.id) return deleteItemAction(item).then(event => { if (event === 'yes') redirectToBase(); });
		return Promise.resolve();
	}


	// Change customer group methods ------------------------------------------------------------------------------------
	/**
	 * Start the interval to periodically check if a customer group is being changed
	 * @return {void}
	 */
	startChangeInterval() {
		const checkChangeInterval = this.getOption('checkChangeInterval');
		const changeIntervalId = this.getOption('changeIntervalId');

		// Clear previous interval (if any)
		if (changeIntervalId) clearInterval(changeIntervalId);

		// Start the interval and store its identifier in a component option
		if (checkChangeInterval) {
			this.setOption(
				'changeIntervalId',
				setInterval(() => this.updateChangeProgress(false, true), checkChangeInterval)
			);
		}
	}
	
	/**
	 * Clear interval that checks customer group change status
	 */
	stopChangeInterval() {
		clearInterval(this.getOption('changeIntervalId'));
	}

	/**
	 * Update the progress of changing customer group
	 *
	 * @param {boolean} [onMount=false] - Flag that specifies if this method is called as part of the mount cycle.
	 * @param {boolean} [ignoreErrors=false] - Flag that specifies if response errors are ignored.
	 * @return {Promise<CustomerGroupChangeRequestDataObject[]|undefined>}
	 */
	updateChangeProgress(onMount = false, ignoreErrors = false) {
		const {customerGroupsItemId, loadCustomerGroupChangeInProgressRequestsAction} = this.props;
		const executeAbortableAction = (onMount ? this.executeAbortableActionMount : this.executeAbortableAction);
		
		if (!!customerGroupsItemId) {
			// Abort previous check action
			this.abortAction('loadCustomerGroupChangeInProgressRequestsAction');

			// Execute check action
			return executeAbortableAction({
				id: 'loadCustomerGroupChangeInProgressRequestsAction',
				action: loadCustomerGroupChangeInProgressRequestsAction
			}, customerGroupsItemId, ignoreErrors)
				.then(
					/**
					 * @param {CustomerGroupChangeRequestDataObject[]|undefined} inProgressChangeRequests
					 * @return {Promise<CustomerGroupChangeRequestDataObject[]|undefined>}
					 */
					inProgressChangeRequests => {
						const inProgress = (getArray(inProgressChangeRequests).length > 0);
						
						// Speed up the interval while change is in progress
						if (inProgress) this.setOption('checkChangeInterval', this.getOption('checkChangeIntervalFast'));
						// Slow down the interval if change is not in progress
						else this.setOption('checkChangeInterval', this.getOption('checkChangeIntervalSlow'));
	
						// Restart the interval with a new time
						this.startChangeInterval();
						
						return inProgressChangeRequests;
					});
		}
		return Promise.resolve(undefined);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
ItemPopup.propTypes = {
	// Flag that specifies if this popup is for creating a new item
	// @note This prop can be dynamically changed.
	isNew: PropTypes.bool,
	// Function that will redirect to item URL
	redirectToItem: PropTypes.func, // Arguments: {string} - Item id to redirect to.
	// Function that will redirect to the base URL of the page
	redirectToBase: PropTypes.func, // Arguments: no arguments
	// ID of the project
	// @description If project ID is specified, this popup is used in the project-related page.
	projectId: PropTypes.string,
	
	// Actions
	// @param {CustomerGroupsItemDataObject} item - Item to delete it. 
	// A full item is needed to access the name displayed in a delete confirmation message.
	// @return {Promise<void>}
	deleteItemAction: PropTypes.func,

	// Events
	onClose: PropTypes.func,
	onGlobalAction: PropTypes.func,
	onTabAction: PropTypes.func,
};

export default connect(
	mapStateToProps, getPageActions({...pageActions, ...actions}), null, {forwardRef: true}
)(ItemPopup);